/**
 * Video player (§20).
 *
 * The `<video>` element is the platform's; the app decodes nothing and edits
 * nothing. `canPlayType` is asked before the controls are trusted, so a codec
 * the device lacks produces an honest message rather than a black rectangle
 * that never plays (§19). The metadata beside it — length, dimensions, codec —
 * is read from the container without decoding a frame.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { VideoModel } from '@moduviewer/handlers';
import { videoHeadline } from '@moduviewer/handlers';
import { Icon } from '@moduviewer/design/icon';

export interface VideoViewerProps {
  readonly model: VideoModel;
  readonly bytes: Uint8Array;
}

function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function VideoViewer({ model, bytes }: VideoViewerProps): React.JSX.Element {
  const video = useRef<HTMLVideoElement>(null);
  const [playable, setPlayable] = useState<boolean | null>(null);

  // Played from memory, never the network (§29); the URL is revoked on change.
  const url = useMemo(
    () => URL.createObjectURL(new Blob([bytes as BlobPart], { type: model.mimeType })),
    [bytes, model.mimeType],
  );
  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    setPlayable(el.canPlayType(model.mimeType) !== '');
  }, [model.mimeType, url]);

  const ratio = model.width && model.height ? model.width / model.height : 16 / 9;

  return (
    <div className="video">
      <div className="video__stage" style={{ aspectRatio: String(ratio) }}>
        <video
          ref={video}
          className="video__player"
          src={url}
          controls
          preload="metadata"
          aria-label={videoHeadline(model) || '동영상 재생'}
        />
      </div>

      {playable === false ? (
        <p className="video__unsupported" role="status">
          <Icon name="alert" size={15} />
          이 기기의 코덱으로는 이 동영상을 재생할 수 없습니다. 다른 앱에서 열어 주세요 (§20).
        </p>
      ) : null}

      <dl className="video__specs tnum">
        <div><dt>길이</dt><dd>{formatTime(model.durationSec)}</dd></div>
        {model.width && model.height ? <div><dt>해상도</dt><dd>{model.width}×{model.height}</dd></div> : null}
        {model.videoCodec ? <div><dt>영상 코덱</dt><dd>{model.videoCodec}</dd></div> : null}
        {model.hasAudio && model.audioCodec ? <div><dt>음성 코덱</dt><dd>{model.audioCodec}</dd></div> : null}
      </dl>

      <p className="video__note">
        {/* §19/§20 — say what this build does and does not do with video. */}
        이 버전은 동영상을 재생만 하며 편집하지 않습니다. 재생은 기기의 코덱에 맡깁니다.
      </p>
    </div>
  );
}
